import "@/i18n";

import { DirectionProvider } from "@radix-ui/react-direction";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { Toaster as Sonner } from "@/components/ui/sonner";

import { ThemeProvider } from "./components/ui/theme-provider";
import { AuthProvider } from "./providers/AuthProvider";
import CallAssistantTourProvider from "./providers/CallAssistantTourProvider";
import MainTourProvider from "./providers/MainTourProvider";
import MediaDownloadAuthSync from "./providers/MediaDownloadAuthSync";
import MediaDownloadProvider from "./providers/MediaDownloadProvider";
import MeetingUploadProvider from "./providers/MeetingUploadProvider";
import { ModelSettingsProvider } from "./providers/ModelSettingsProvider";
import { NavigationGuardProvider } from "./providers/NavigationGuardProvider";
import { PiiCustomEntityLabelsProvider } from "./providers/PiiCustomEntityLabelsProvider";
import ProductAnalyticsProvider from "./providers/ProductAnalyticsProvider";
import { QueryProvider } from "./providers/QueryProvider";
import { AppRoutes } from "./routes";
import AdminGate from "./routes/AdminGate";

const App = (): ReactNode => {
  const { i18n } = useTranslation();
  const dir = i18n.dir();

  return (
    <DirectionProvider dir={dir}>
      <BrowserRouter>
        <NavigationGuardProvider>
          <QueryProvider>
            {/* NOTE: MediaDownloadProvider wraps the whole route switch (not just
                the "/*" element) so an in-flight media download and its progress
                survive even the /admin/* ↔ /* boundary, not only navigation
                within the regular app (NSAI-3838). */}
            <MediaDownloadProvider>
              <Routes>
                {/* NOTE: Admin routes - always registered to avoid fallthrough during config load */}
                <Route path="/admin/*" element={<AdminGate />} />

                {/* NOTE: Regular app routes */}
                <Route
                  path="/*"
                  element={
                    <AuthProvider>
                      {/* NOTE: NSAI-4838 — inside AuthProvider so it can read the
                          current project; provides the per-(user, project) model
                          defaults app-wide. */}
                      <ModelSettingsProvider>
                        <ThemeProvider>
                          <MainTourProvider>
                            <CallAssistantTourProvider>
                              {/* NOTE: Inside AuthProvider — purges downloads from
                                  the app-level provider on logout / account switch. */}
                              <MediaDownloadAuthSync />
                              {/* NOTE: NSAI-4614 — inside AuthProvider so it
                                  can identify the user and their project, and
                                  inside BrowserRouter so it can emit an SPA
                                  pageview per navigation. Renders nothing. */}
                              <ProductAnalyticsProvider />
                              <Sonner />
                              {/* NOTE: Wraps the route switch so a meeting
                                  upload started on /uploads keeps running when
                                  the user navigates away — e.g. to open an
                                  already-transcribed recording while the rest
                                  of a batch is still processing (NSAI-5141).
                                  Inside AuthProvider so it can purge uploads
                                  on logout / account switch. */}
                              <MeetingUploadProvider>
                                {/* NOTE: NSAI-6787 — inside AuthProvider so it
                                    can resolve the active organization; names
                                    custom PII entity types for every chip and
                                    summary in chat, Security Analytics and the
                                    Anonymization Lab. */}
                                <PiiCustomEntityLabelsProvider>
                                  <AppRoutes />
                                </PiiCustomEntityLabelsProvider>
                              </MeetingUploadProvider>
                            </CallAssistantTourProvider>
                          </MainTourProvider>
                        </ThemeProvider>
                      </ModelSettingsProvider>
                    </AuthProvider>
                  }
                />
              </Routes>
            </MediaDownloadProvider>
          </QueryProvider>
        </NavigationGuardProvider>
      </BrowserRouter>
    </DirectionProvider>
  );
};

export default App;
